import React, { useState, useEffect, useRef } from "react";
import { Alert, Spinner, Modal, Button } from "react-bootstrap";
import { DatePicker } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import RIcon from "./components/ReservasIcons";
import MarketingOptIn from "./components/MarketingOptIn";
import { SucursalBanner, SucursalesSection } from "./components/Sucursales";
import { backTarget } from "./components/sucursalesModel";
import ReviewsSection from "./components/ReviewsSection";
import WaitlistJoin from "../listaDeEspera/WaitlistJoin";
import { isWaitlistEnabled } from "../listaDeEspera/waitlistModel";
import { ONLINE_BOOKING_DISABLED_MESSAGE, isOnlineBookingDisabled } from "./onlineBooking";
import {
  HoldCountdown,
  QtySelect,
  COUNTRIES,
  FALLBACK_IMAGE,
  WEEK_DAYS_ES,
  resolveUrl,
} from "./components/ReservaShared";
import "./reservas-mobile.css";

// Ordered list of mobile wizard steps
const STEPS = ["landing", "personas", "fecha", "horario", "zona", "datos"];

const STEP_LABELS = {
  landing: null,
  personas: "Personas",
  fecha: "Fecha",
  horario: "Horario",
  zona: "Sector",
  datos: "Tus datos",
};

// Steps where progress bar is shown (excludes landing)
const PROGRESS_STEPS = ["personas", "fecha", "horario", "zona", "datos"];

/**
 * Mobile reservation wizard (≤768px). Receives the shared `flow` object
 * from useReservationFlow. One decision per screen, full-height layout.
 */
export default function BarDetailMobile({ flow, heroBar = null, sedes = null }) {
  const navigate = useNavigate();

  const {
    bar,
    selectedDate, setSelectedDate,
    partySize, setPartySize,
    minDate, maxDate, dateLabel,
    availability,
    loadingAvail, availError, loadAvailability,
    availableSlotsData, availableZonesForSlot,
    selectedSlot, setSelectedSlot,
    selectedZone, setSelectedZone,
    holdExpiresAt, holdLoading, holdError, handleAdvanceToForm,
    handleHoldExpired,
    register, handleSubmit, errors, trigger,
    submitting, submitError,
    consent, setConsent,
    marketingOptIn, setMarketingOptIn,
    onSubmit,
    selectedCountry, setSelectedCountry,
    countryDropdownOpen, setCountryDropdownOpen, countryRef,
    showPaymentModal, setShowPaymentModal,
    modalCard, setModalCard,
    cardOptions, selectedCardOption,
    pendingValues, setPendingValues,
    handleConfirmPaymentSelection,
    displayPrice, getZonePrice,
    restoredStep,
    sucursales = [],
    casaCentral = null,
  } = flow;

  // Bars are bookable unless explicitly turned off (clubs are the opposite: opt-in).
  const bookingDisabled = isOnlineBookingDisabled(bar);

  // Ref for the inline calendar container in the "fecha" step
  const calRef = useRef(null);

  // Initialize step from restored session
  const [step, setStep] = useState(() => {
    if (restoredStep === "form") return "datos";
    if (restoredStep === "slots") return "horario";
    return "landing";
  });

  // If restoredStep arrives after bar loads async, sync once
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    if (restoredStep === "form") { setStep("datos"); restoredRef.current = true; }
    else if (restoredStep === "slots") { setStep("horario"); restoredRef.current = true; }
  }, [restoredStep]);

  // Scroll to top on step changes
  useEffect(() => {
    const resetScroll = () => {
      // Reset window scroll
      window.scrollTo(0, 0);
      
      // Reset scroll on all potential scroll containers (both inner and outer)
      const selectors = [
        ".app-shell",
        ".app-content",
        ".rmob-step-body",
        ".rmob-landing-body",
        ".reservas-root",
        "#root"
      ];
      selectors.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) {
          el.scrollTo(0, 0);
          el.scrollTop = 0;
        }
      });
    };

    resetScroll();
    // Run it on multiple ticks to be absolutely sure after DOM and animation finishes
    const timeoutId1 = setTimeout(resetScroll, 0);
    const timeoutId2 = setTimeout(resetScroll, 100);
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  }, [step]);

  // Lock body overflow when in wizard steps (step !== "landing")
  useEffect(() => {
    if (step !== "landing") {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [step]);

  // Advance to next step
  const goNext = (target) => setStep(target);

  // Go back one step
  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx <= 0) { navigate(backTarget("reservas", casaCentral).path); return; }
    setStep(STEPS[idx - 1]);
  };

  const handleHoldExpiredLocal = () => {
    handleHoldExpired();
    setStep("horario");
  };

  const handleAdvanceToFormLocal = async (zoneName) => {
    const result = await handleAdvanceToForm(zoneName);
    // navigate to login was triggered — do nothing, auth will restore us
    if (!result) return;
    if (result?.reused || result?.created) {
      setStep("datos");
    }
  };

  const onSubmitLocal = async (values) => {
    const result = await onSubmit(values);
    if (result === "slots") setStep("horario");
  };

  if (!bar) return null;

  const img = bar.image || bar.mobileimage || FALLBACK_IMAGE;
  const cityName = bar.cityid?.name || (typeof bar.cityid === "string" ? bar.cityid : "");
  // On a casa central the hero stays the casa's while `bar` is the chosen sede.
  const hero = heroBar || bar;
  const heroImg = hero.image || hero.mobileimage || FALLBACK_IMAGE;
  const heroCity = hero.cityid?.name || (typeof hero.cityid === "string" ? hero.cityid : "");
  const openingHours = Array.isArray(bar.openingHours) ? bar.openingHours : [];
  const todayIndex = dayjs().day();
  const progressIdx = PROGRESS_STEPS.indexOf(step);

  // ---- Shared header ----
  const Header = ({ title, subtitle }) => (
    <div className="rmob-header">
      <button className="rmob-back" onClick={goBack} aria-label="Volver">
        <RIcon name="chevL" width={20} height={20} />
      </button>
      <div className="rmob-header-center">
        {title && <span className="rmob-header-title">{title}</span>}
        {subtitle && <span className="rmob-header-sub">{subtitle}</span>}
      </div>
      <div className="rmob-header-spacer" />
    </div>
  );

  // ---- Progress bar (shown for steps after landing) ----
  const Progress = () => {
    if (progressIdx < 0) return null;
    const pct = Math.round(((progressIdx + 1) / PROGRESS_STEPS.length) * 100);
    return (
      <div className="rmob-progress">
        <div className="rmob-progress-bar" style={{ width: `${pct}%` }} />
      </div>
    );
  };

  // ---- Summary pill shown in CTA area ----
  const SummaryPill = () => {
    const parts = [];
    if (selectedDate) parts.push(selectedDate.format("DD/MM"));
    if (selectedSlot) parts.push(selectedSlot.slotStart);
    if (partySize) parts.push(`${partySize} pers.`);
    if (selectedZone) parts.push(selectedZone);
    if (!parts.length) return null;
    return <div className="rmob-summary-pill">{parts.join(" · ")}</div>;
  };

  // ================================================================
  // LANDING — Bar hero + info + "Reservar" CTA
  // ================================================================
  if (step === "landing") {
    return (
      <div className="reservas-root rmob-root">
        <SucursalBanner kind="reservas" casaCentral={casaCentral} />
        <div className="rmob-landing-hero">
          <img src={heroImg} alt={hero.name} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
          <div className="rmob-hero-scrim" />
          <button className="rmob-back rmob-back-hero" onClick={() => navigate(backTarget("reservas", casaCentral).path)} aria-label="Volver">
            <RIcon name="chevL" width={20} height={20} />
          </button>
          <div className="rmob-hero-info">
            <h1 className="rmob-hero-name">{hero.name}</h1>
            <div className="rmob-hero-meta">
              {hero.address && <span><RIcon name="pin" width={14} height={14} /> {hero.address}{heroCity ? `, ${heroCity}` : ""}</span>}
              {hero.reservationMode === "deposit" && hero.depositAmount > 0 && (
                <span><RIcon name="dollar" width={14} height={14} /> Desde ${Number(hero.depositAmount).toLocaleString("es-AR")}</span>
              )}
            </div>
          </div>
        </div>

        <div className="rmob-landing-body">
          <SucursalesSection kind="reservas" sucursales={sucursales} id="sec-sucursales" sedes={sedes} />

          {/* Description */}
          <div className="rmob-section">
            <h3 className="rmob-section-title">Descripción</h3>
            <p className="rmob-desc">{bar.description || bar.shortDescription || "Bar disponible para reservas."}</p>
          </div>

          {/* Amenities */}
          {Array.isArray(bar.amenities) && bar.amenities.length > 0 && (
            <div className="rmob-section rmob-tags">
              {bar.amenities.map((t) => <span className="bar-tag" key={t}>{t}</span>)}
            </div>
          )}

          {/* Gallery */}
          {Array.isArray(bar.galleryimage) && bar.galleryimage.length > 0 && (
            <div className="rmob-section" style={{ paddingRight: 0 }}>
              <h3 className="rmob-section-title">Galería</h3>
              <div className="rmob-gallery-carousel">
                {bar.galleryimage.map((url, idx) => (
                  <div key={idx} className="rmob-gallery-item">
                    <img src={resolveUrl(url)} alt={`${bar.name} foto ${idx + 1}`} onError={(e) => { e.currentTarget.src = FALLBACK_IMAGE; }} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Horarios */}
          {openingHours.length > 0 && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Horarios</h3>
              <div className="hours-list-premium">
                {openingHours.slice().sort((a, b) => a.weekday - b.weekday).map((h, i) => (
                  <div className={`hours-row-premium${h.weekday === todayIndex ? " today" : ""}${!h.enabled ? " closed" : ""}`} key={i}>
                    <span className="d">{WEEK_DAYS_ES[h.weekday]}</span>
                    <span className="h">{h.enabled ? `${h.open} – ${h.close}` : "Cerrado"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Contact info */}
          {(bar.contactPhone || bar.contactEmail || bar.instagram || bar.website) && (
            <div className="rmob-section">
              <h3 className="rmob-section-title">Contacto</h3>
              <div className="rmob-contact-list">
                {bar.contactPhone && <div className="rmob-contact-row"><RIcon name="phone" width={16} height={16} /><span>{bar.contactPhone}</span></div>}
                {bar.contactEmail && <div className="rmob-contact-row"><RIcon name="mail" width={16} height={16} /><span>{bar.contactEmail}</span></div>}
                {bar.instagram && <div className="rmob-contact-row"><RIcon name="instagram" width={16} height={16} /><a href={`https://instagram.com/${bar.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">@{bar.instagram.replace('@', '')}</a></div>}
              </div>
            </div>
          )}

          <ReviewsSection barId={bar._id} isDesktop={false} />

          {/* Policies */}
          <div className="rmob-section rmob-policies">
            {(bar.cancellationAllowed === false || bar.cancellationAllowed === "false") ? (
              <>
                <div className="policy"><RIcon name="cal" width={15} height={15} />Reservas hasta con {bar.reservationWindowDays || 6} días de antelación.</div>
                <div className="policy"><RIcon name="alert" width={15} height={15} style={{ color: "var(--zoco-danger)" }} />Las reservas no son cancelables.</div>
              </>
            ) : (
              <>
                <div className="policy"><RIcon name="cal" width={15} height={15} />Reservas hasta {bar.reservationWindowDays || 6} días de antelación.</div>
                <div className="policy"><RIcon name="shield" width={15} height={15} />Cancelación sin cargo hasta {bar.cancellationPolicyHours || 24} hs antes.</div>
              </>
            )}
            {bar.additionalNotes && <div className="policy"><RIcon name="info" width={15} height={15} />{bar.additionalNotes}</div>}
          </div>

          {/* Extra bottom padding for sticky CTA */}
          <div style={{ height: 100 }} />
        </div>

        <div className="rmob-cta-bar">
          {bookingDisabled && (
            <div className="rs-note" role="note" style={{ margin: "0 0 10px", color: "var(--zoco-gray-2)" }}>
              <RIcon name="info" width={15} height={15} />
              {ONLINE_BOOKING_DISABLED_MESSAGE}
            </div>
          )}
          <button
            className="r-btn-primary rmob-cta-btn"
            onClick={() => goNext("personas")}
            disabled={bookingDisabled}
            aria-disabled={bookingDisabled}
          >
            Reservar <RIcon name="arrowR" width={18} height={18} />
          </button>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: PERSONAS
  // ================================================================
  if (step === "personas") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="¿Cuántas personas?" subtitle={bar.name} />
        <Progress />

        <div className="rmob-step-body rmob-step-centered">
          <div className="rmob-qty-block">
            <button
              className="rmob-qty-btn"
              onClick={() => setPartySize((n) => Math.max(1, n - 1))}
              disabled={partySize <= 1}
            >−</button>
            <span className="rmob-qty-val">{partySize}</span>
            <button
              className="rmob-qty-btn"
              onClick={() => setPartySize((n) => Math.min(bar.maxPartySize || 20, n + 1))}
              disabled={partySize >= (bar.maxPartySize || 20)}
            >+</button>
          </div>
          <p className="rmob-qty-label">{partySize === 1 ? "persona" : "personas"}</p>
          {bar.maxPartySize && (
            <p className="rmob-qty-hint">Máximo {bar.maxPartySize} personas por reserva</p>
          )}
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button className="r-btn-primary rmob-cta-btn" onClick={() => goNext("fecha")}>
              Continuar <RIcon name="arrowR" width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: FECHA
  // ================================================================
  if (step === "fecha") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="¿Qué día?" subtitle={bar.name} />
        <Progress />

        <div className="rmob-step-body">
          <div className="rmob-date-quick">
            <button
              className={`rmob-date-chip${selectedDate.isSame(dayjs().startOf("day"), "day") ? " active" : ""}`}
              onClick={() => setSelectedDate(dayjs().startOf("day"))}
            >Hoy</button>
            <button
              className={`rmob-date-chip${selectedDate.isSame(dayjs().add(1, "day").startOf("day"), "day") ? " active" : ""}`}
              onClick={() => setSelectedDate(dayjs().add(1, "day").startOf("day"))}
            >Mañana</button>
          </div>

          <div className="rmob-calendar-wrap" ref={calRef}>
            <DatePicker
              value={selectedDate}
              onChange={(d) => d && setSelectedDate(d.startOf("day"))}
              disabledDate={(current) => current && (current.isBefore(minDate, "day") || current.isAfter(maxDate, "day"))}
              open={true}
              allowClear={false}
              getPopupContainer={() => calRef.current || document.body}
              style={{ display: "none" }}
              dropdownClassName="rmob-calendar-inline"
              popupClassName="rmob-calendar-inline"
            />
          </div>

          <div className="rmob-selected-date">
            <RIcon name="cal" width={16} height={16} />
            {selectedDate.format("dddd DD [de] MMMM")}
          </div>
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              className="r-btn-primary rmob-cta-btn"
              onClick={() => { loadAvailability(selectedDate); goNext("horario"); }}
            >
              Ver horarios <RIcon name="arrowR" width={18} height={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: HORARIO
  // ================================================================
  if (step === "horario") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="¿A qué hora?" subtitle={bar.name} />
        <Progress />

        <div className="rmob-step-body">
          {loadingAvail ? (
            <div className="rmob-loading"><Spinner animation="border" size="sm" /> Buscando horarios…</div>
          ) : availError ? (
            <Alert variant="warning">{availError}</Alert>
          ) : availableSlotsData.length === 0 ? (
            <div className="rmob-empty">
              <RIcon name="clock" width={32} height={32} />
              <p>No hay horarios para {selectedDate.format("DD/MM/YYYY")}.<br />Probá con otra fecha.</p>
              <button className="r-btn-soft" onClick={() => setStep("fecha")}>Cambiar fecha</button>
            </div>
          ) : (
            <div className="rmob-slots-list">
              {availableSlotsData.map((slot) => (
                <button
                  key={slot.slotStart}
                  type="button"
                  className={`rmob-slot-row${selectedSlot?.slotStart === slot.slotStart ? " selected" : ""}${!slot.hasAvailability ? " unavailable" : ""}`}
                  disabled={!slot.hasAvailability}
                  onClick={() => {
                    setSelectedSlot(slot);
                    setSelectedZone(null);
                    goNext("zona");
                  }}
                >
                  <span className="rmob-slot-time">{slot.slotStart} hs</span>
                  <span className="rmob-slot-end">hasta {slot.slotEnd}</span>
                  {!slot.hasAvailability
                    ? <span className="rmob-slot-tag">Sin lugar</span>
                    : <RIcon name="chevR" width={16} height={16} className="rmob-slot-arrow" />
                  }
                </button>
              ))}
            </div>
          )}
          <WaitlistJoin enabled={isWaitlistEnabled(bar)} barId={bar._id} kind="table" date={dateLabel} criteria={{ partySize: Number(partySize) }} summary={`${partySize} persona${Number(partySize) === 1 ? "" : "s"}`} />
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            {selectedSlot && (
              <button className="r-btn-primary rmob-cta-btn" onClick={() => goNext("zona")}>
                Continuar <RIcon name="arrowR" width={18} height={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: ZONA
  // ================================================================
  if (step === "zona") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="¿Qué sector?" subtitle={bar.name} />
        <Progress />

        <div className="rmob-step-body">
          {!selectedSlot ? (
            <div className="rmob-empty">
              <p>Primero elegí un horario.</p>
              <button className="r-btn-soft" onClick={() => setStep("horario")}>Elegir horario</button>
            </div>
          ) : availableZonesForSlot.length === 0 ? (
            <div className="rmob-empty">
              <RIcon name="pin" width={32} height={32} />
              <p>No hay sectores disponibles para este horario.</p>
              <button className="r-btn-soft" onClick={() => setStep("horario")}>Cambiar horario</button>
            </div>
          ) : (
            <div className="rmob-zones-list">
              {(availability.zones || []).map((zone) => {
                const slotData = zone.slots?.find((s) => s.slotStart === selectedSlot.slotStart);
                const available = slotData?.available ?? false;
                const count = slotData?.availableCount ?? 0;
                const isSelected = selectedZone === zone.name;
                const zonePrice = getZonePrice(zone.name);
                return (
                  <button
                    key={zone.name}
                    type="button"
                    className={`rmob-zone-row${isSelected ? " selected" : ""}${!available ? " unavailable" : ""}`}
                    disabled={!available}
                    onClick={async () => {
                      setSelectedZone(zone.name);
                      await handleAdvanceToFormLocal(zone.name);
                    }}
                  >
                    <div className="rmob-zone-info">
                      <span className="rmob-zone-name">{zone.name}</span>
                      {available
                        ? <span className="rmob-zone-avail">{count} {count === 1 ? "lugar" : "lugares"}</span>
                        : <span className="rmob-zone-tag">Sin lugar</span>
                      }
                    </div>
                    <div className="rmob-zone-right">
                      {zonePrice && <span className="rmob-zone-price">{zonePrice}</span>}
                      {available && <RIcon name="chevR" width={16} height={16} />}
                    </div>
                    {holdLoading && isSelected && <Spinner animation="border" size="sm" className="rmob-hold-spinner" />}
                  </button>
                );
              })}
            </div>
          )}
          {holdError && <Alert variant="danger" className="mt-3">{holdError}</Alert>}
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack} style={{ width: "100%" }}>
              <RIcon name="chevL" width={16} height={16} /> Volver a horarios
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ================================================================
  // STEP: DATOS (form)
  // ================================================================
  if (step === "datos") {
    return (
      <div className="reservas-root rmob-root rmob-step-fixed">
        <Header title="Tus datos" subtitle={bar.name} />
        <Progress />

        {holdExpiresAt && (
          <div className="rmob-countdown-bar">
            <HoldCountdown expiresAt={holdExpiresAt} onExpired={handleHoldExpiredLocal} />
          </div>
        )}

        <div className="rmob-step-body rmob-form-body">
          {/* Reservation summary */}
          <div className="rmob-reserva-summary">
            <div className="rmob-rs-bar-name">{bar.name}</div>
            {selectedDate && <div className="rmob-rs-row"><RIcon name="cal" width={14} /><span>{selectedDate.format("DD/MM/YYYY")}</span></div>}
            {selectedSlot && <div className="rmob-rs-row"><RIcon name="clock" width={14} /><span>{selectedSlot.slotStart} hs · hasta {selectedSlot.slotEnd}</span></div>}
            {selectedZone && <div className="rmob-rs-row"><RIcon name="pin" width={14} /><span>{selectedZone}</span></div>}
            <div className="rmob-rs-row"><RIcon name="users" width={14} /><span>{partySize} {partySize === 1 ? "persona" : "personas"}</span></div>
            {displayPrice && bar.reservationMode === "deposit" && (
              <div className="rmob-rs-row rmob-rs-price"><RIcon name="dollar" width={14} /><span><strong>{displayPrice}</strong> de seña</span></div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmitLocal)} id="mob-reservation-form">
            <div className="rmob-field">
              <label>Nombre y apellido <span className="req">*</span></label>
              <div className="inp">
                <RIcon name="user" />
                <input placeholder="Ej. Camila Rodríguez" {...register("name", { required: "Ingresá tu nombre." })} />
              </div>
              {errors.name && <span className="rmob-field-error">{errors.name.message}</span>}
            </div>

            <div className="rmob-field">
              <label>Email <span className="req">*</span></label>
              <div className="inp">
                <RIcon name="mail" />
                <input type="email" placeholder="tu@email.com" {...register("email", { required: "Ingresá un email válido.", pattern: { value: /^\S+@\S+\.\S+$/, message: "Email inválido." } })} />
              </div>
              {errors.email && <span className="rmob-field-error">{errors.email.message}</span>}
            </div>

            <div className="rmob-field">
              <label>Teléfono <span className="req">*</span></label>
              <div className={`inp${errors.phone ? " is-invalid" : ""}`} style={{ paddingLeft: 0, position: "relative" }}>
                <div
                  ref={countryRef}
                  style={{ display: "flex", alignItems: "center", gap: "8px", paddingLeft: "14px", paddingRight: "12px", height: "100%", background: "#f9fafb", borderTopLeftRadius: "9px", borderBottomLeftRadius: "9px", userSelect: "none", borderRight: "1px solid var(--zoco-border, #E5E7EB)", flexShrink: 0, cursor: "pointer", position: "relative" }}
                  onClick={() => setCountryDropdownOpen(o => !o)}
                >
                  {selectedCountry.flag}
                  <span style={{ fontSize: "13.5px", fontWeight: "700", color: "#2d3035", whiteSpace: "nowrap" }}>{selectedCountry.label}</span>
                  <svg width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ marginLeft: "2px", transform: countryDropdownOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                    <path d="M1 1L4 4L7 1" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {countryDropdownOpen && (
                    <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#ffffff", border: "1px solid #E5E7EB", borderRadius: "10px", boxShadow: "0 10px 25px rgba(0,0,0,0.12)", zIndex: 1000, minWidth: "160px", padding: "6px 0", maxHeight: "200px", overflowY: "auto" }}>
                      {COUNTRIES.map((c) => (
                        <button key={c.code} type="button" onClick={(e) => { e.stopPropagation(); setSelectedCountry(c); setCountryDropdownOpen(false); setTimeout(() => { trigger("phone"); }, 0); }} style={{ display: "flex", alignItems: "center", width: "100%", padding: "8px 14px", border: "none", background: selectedCountry.code === c.code ? "#f3f4f6" : "#ffffff", cursor: "pointer", gap: "10px", textAlign: "left" }}>
                          {c.flag}
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#2d3035", flex: 1 }}>{c.label}</span>
                          {selectedCountry.code === c.code && <span style={{ color: "#00b159", fontWeight: "bold", fontSize: "14px" }}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input type="tel" placeholder={selectedCountry.placeholder} style={{ border: "none", outline: "none", height: "100%", width: "100%", paddingLeft: "12px", fontSize: "14.5px", fontWeight: "600", color: "var(--zoco-ink, #2d3035)", background: "transparent" }}
                  {...register("phone", {
                    required: "Ingresá tu teléfono.",
                    validate: (val) => {
                      return selectedCountry.validate(val) || "Este número no es válido";
                    }
                  })}
                />
              </div>
              {errors.phone && <span className="rmob-field-error">{errors.phone.message}</span>}
            </div>

            <div className="rmob-field">
              <label>DNI <span className="req">*</span></label>
              <div className="inp">
                <RIcon name="user" />
                <input
                  placeholder="Tu DNI"
                  onInput={(e) => {
                    e.target.value = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                  }}
                  {...register("dni", {
                    required: "Ingresá tu DNI.",
                    pattern: { value: /^\d{7,10}$/, message: "DNI inválido (debe tener entre 7 y 10 números)." },
                  })}
                />
              </div>
              {errors.dni && <span className="rmob-field-error">{errors.dni.message}</span>}
            </div>

            <div className="rmob-field">
              <label>Notas adicionales</label>
              <div className="inp" style={{ height: "auto", padding: "12px 14px" }}>
                <textarea placeholder="Cumpleaños, alergias, preferencias…" rows={2} style={{ border: "none", outline: "none", width: "100%", resize: "none", fontFamily: "inherit", fontSize: 14 }} {...register("notes")} />
              </div>
            </div>

            <MarketingOptIn venueName={bar.name} checked={marketingOptIn} onChange={setMarketingOptIn} />

            <div className="consent" onClick={() => setConsent((c) => !c)}>
              <span className={`cbox${consent ? " on" : ""}`}>{consent && <RIcon name="check" width={13} height={13} />}</span>
              <p>Acepto la <strong>política de reservas</strong> de {bar.name}{(bar.cancellationAllowed !== false && bar.cancellationAllowed !== "false") ? `: cancelación sin cargo hasta ${bar.cancellationPolicyHours || 24} hs antes.` : ": las reservas no son cancelables."}</p>
            </div>

            {submitError && <Alert variant="danger" className="mt-3">{submitError}</Alert>}
          </form>

          <div style={{ height: 100 }} />
        </div>

        <div className="rmob-cta-bar">
          <SummaryPill />
          <div className="rmob-cta-actions">
            <button type="button" className="r-btn-soft rmob-back-btn" onClick={goBack}>
              <RIcon name="chevL" width={16} height={16} /> Volver
            </button>
            <button
              type="submit"
              form="mob-reservation-form"
              className="r-btn-primary rmob-cta-btn"
              disabled={submitting || !consent}
              style={{ opacity: consent ? 1 : 0.5 }}
            >
              {submitting
                ? <Spinner animation="border" size="sm" />
                : <>{bar.reservationMode === "deposit" ? "Ir al pago" : "Confirmar reserva"} <RIcon name="arrowR" width={18} height={18} /></>
              }
            </button>
          </div>
        </div>

        {/* Fiserv Payment Modal */}
        <Modal show={showPaymentModal} onHide={() => { setShowPaymentModal(false); setPendingValues(null); }} centered backdrop="static" className="payment-modal">
          <Modal.Header closeButton><Modal.Title>Elegí cómo pagar</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Tarjeta</label>
              <QtySelect className="payment-select" placeholder="Elegí una tarjeta" value={modalCard} onChange={(val) => setModalCard(val == null ? "" : String(val))} options={(cardOptions || []).map((opt) => ({ value: opt.id, label: opt.label, disabled: opt.disabled }))} />
            </div>
            {selectedCardOption && (
              <div className="mb-3">
                <label className="form-label">Cuotas</label>
                <QtySelect className="payment-select" placeholder="Elegí la cantidad de cuotas" value="1" disabled={true} onChange={() => { }} options={[{ value: "1", label: "1 cuota (sin interés)" }]} />
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowPaymentModal(false); setPendingValues(null); }}>Cancelar</Button>
            <Button variant="primary" onClick={handleConfirmPaymentSelection} disabled={!selectedCardOption}>Continuar</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  return null;
}
